import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import FolioRule from "@/components/FolioRule";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCellar() {
  const { token } = useAuth();
  const { t } = useI18n();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/cellar`, { headers })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await axios.delete(`${API}/admin/cellar/${id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (_e) {
      toast.error("Failed");
    }
  };

  const downloadCsv = () => {
    const head = "email,lang,source,created_at\n";
    const body = rows
      .map((r) => `"${r.email}","${r.lang || ""}","${r.source}","${r.created_at}"`)
      .join("\n");
    const blob = new Blob([head + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `maestros-cellar-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-cellar-page">
      <FolioRule label="03 / CELLAR" className="mb-12" />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="font-display text-5xl md:text-6xl leading-tight text-cream">
          {t("admin.cellar")}
        </h1>
        <button
          type="button"
          onClick={downloadCsv}
          disabled={rows.length === 0}
          className="btn-luxe disabled:opacity-40"
          data-testid="cellar-export"
        >
          Export CSV
          <span className="arrow-tick" />
        </button>
      </div>

      <div className="mt-14 border border-gold/20">
        {loading ? (
          <div className="px-6 py-10 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-10 font-mono text-[10px] tracking-widest2 uppercase text-cream/45">
            {t("admin.no_subscribers")}
          </div>
        ) : (
          rows.map((r, i) => (
            <div
              key={r.id}
              className={`grid md:grid-cols-12 gap-4 px-6 py-5 items-center ${
                i > 0 ? "border-t border-gold/15" : ""
              }`}
              data-testid={`cellar-row-${r.id}`}
            >
              <div className="md:col-span-5 font-display italic text-cream text-lg break-all">
                {r.email}
              </div>
              <div className="md:col-span-2 font-mono text-[10px] tracking-widest2 uppercase text-gold/80">
                {(r.lang || "—").toUpperCase()}
              </div>
              <div className="md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-cream/55">
                {new Date(r.created_at).toLocaleString()}
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  onClick={() => onDelete(r.id)}
                  className="font-mono text-[10px] tracking-widest2 uppercase text-cream/70 hover:text-destructive transition-colors"
                  data-testid={`cellar-delete-${r.id}`}
                >
                  {t("admin.delete")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
